import { useGSAP } from "@gsap/react";
import { gsap } from "gsap";
import { MotionPathPlugin } from "gsap/MotionPathPlugin";
import { Maximize2, RotateCcw, X } from "lucide-react";
import {
  type MouseEvent,
  type PointerEvent,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import type { FindingView } from "../../api/types.js";
import { displayRuleId } from "../../app/rule-id.js";
import {
  buildFindingCompositionGraph,
  type CompositionNode,
  compositionLayout,
} from "./findingCompositionGraph.js";
import {
  cweCatalogHref,
  type ExplainedSourceLine,
  findingSourceHref,
  hopExhibitLines,
  ruleQueueHref,
  snapshotFileHref,
} from "./source-context.js";
import "./finding-attack-story.css";

gsap.registerPlugin(useGSAP, MotionPathPlugin);

function fileName(path: string): string {
  return path.split("/").at(-1) ?? path;
}

function hopStem(value: string): string {
  return fileName(value)
    .replace(/\.[^.]+$/u, "")
    .toLocaleLowerCase();
}

/** Stored excerpt only when this hop owns a finding. Never reuse the sink on every disc. */
export function exhibitSourceForNode(
  node: CompositionNode,
  focus: FindingView,
  related: readonly FindingView[] = [],
): FindingView | null {
  const pool = [focus, ...related];
  if (node.findingId) {
    return pool.find((item) => item.id === node.findingId) ?? null;
  }
  const others = pool.filter((item) => item.id !== focus.id);
  const stem = hopStem(node.label);
  const hits = others.filter((item) => {
    const path = item.location.path;
    if (node.path && (path === node.path || fileName(path) === fileName(node.path))) return true;
    if (stem.length < 4) return false;
    const file = fileName(path).toLocaleLowerCase();
    return file.includes(stem) || path.toLocaleLowerCase().includes(stem);
  });
  return hits[0] ?? null;
}

function exhibitForNode(
  node: CompositionNode,
  focus: FindingView,
  related: readonly FindingView[],
): { finding: FindingView | null; lines: ExplainedSourceLine[] } {
  const source = exhibitSourceForNode(node, focus, related);
  return { finding: source, lines: source ? hopExhibitLines(source) : [] };
}

function excerptEmpty(node: CompositionNode, focus: FindingView): string {
  if (node.tone === "impact") {
    return "Impact is a consequence claim. It has no cited source line.";
  }
  const loc = `${fileName(focus.location.path)}:${focus.location.lineStart}`;
  return `No source coordinate was stored for this individual hop. The finding's cited security-sensitive operation is ${loc}.`;
}

function pathEvidence(
  node: CompositionNode,
  focus: FindingView,
): {
  hopFile: string;
  citedFinding: string;
  status: string;
} {
  return {
    hopFile: node.path
      ? `${node.path}${node.lineStart ? `:${node.lineStart}` : ""}`
      : "Not recorded for this hop",
    citedFinding: `${focus.location.path}:${focus.location.lineStart}`,
    status:
      node.tone === "sibling" ? "Same-scan correlation" : "Recorded on the sealed finding path",
  };
}

function fileHrefForNode(
  node: CompositionNode,
  focus: FindingView,
  related: readonly FindingView[],
): string | null {
  const pool = [focus, ...related];
  const match = node.findingId
    ? pool.find((item) => item.id === node.findingId)
    : node.path
      ? pool.find((item) => item.location.path === node.path)
      : null;
  if (match) return findingSourceHref(match);
  if (node.path) return snapshotFileHref(focus.scanId, node.path, node.lineStart ?? 1);
  return null;
}

function claimBadge(node: CompositionNode): { kicker: string; stamp: string } {
  if (node.tone === "sibling") return { kicker: "Issue briefing", stamp: "Same scan" };
  if (node.tone === "impact") return { kicker: "Issue briefing", stamp: "Claimed" };
  const hop = node.hopNumber ? `Hop ${String(node.hopNumber).padStart(2, "0")}` : "Issue briefing";
  return { kicker: hop, stamp: "Sealed" };
}

function CodeExcerpt({ lines, empty }: { lines: readonly ExplainedSourceLine[]; empty: string }) {
  if (!lines.length) {
    return (
      <p className="finding-attack-story__code-empty" data-testid="finding-attack-code-empty">
        {empty}
      </p>
    );
  }
  return (
    <pre className="finding-attack-story__code" data-testid="finding-attack-code">
      <code>
        {lines.map((line) => (
          <span
            key={line.number}
            className={
              line.cited
                ? "finding-attack-story__code-row is-cited"
                : "finding-attack-story__code-row"
            }
          >
            <span className="finding-attack-story__code-n">{line.number}</span>
            <span className="finding-attack-story__code-s">{line.content.trimStart() || " "}</span>
          </span>
        ))}
      </code>
    </pre>
  );
}

function NodeDossier({
  node,
  spine,
  fileHref,
  lines,
  emptyExcerpt,
  scanId,
  onClose,
  onOpenFinding,
}: {
  node: CompositionNode;
  spine: readonly { id: string; label: string; hopNumber: number | null; here: boolean }[];
  fileHref: string | null;
  lines: readonly ExplainedSourceLine[];
  emptyExcerpt: string;
  scanId: string;
  onClose: () => void;
  onOpenFinding?: ((id: string) => void) | undefined;
}) {
  const badge = claimBadge(node);
  const proofTone =
    node.tone === "sibling"
      ? "warn"
      : node.tone === "impact"
        ? "warn"
        : node.replay === "matched"
          ? "ok"
          : "neutral";
  const fileLabel = node.path
    ? `${fileName(node.path)}${node.lineStart ? `:${node.lineStart}` : ""}`
    : null;
  const openFinding = () => {
    onClose();
    onOpenFinding?.(node.openFindingId);
  };
  return (
    <div
      className="finding-attack-story__pop"
      role="presentation"
      data-testid="finding-attack-dossier"
      onPointerDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <article
        className="finding-attack-story__brief"
        role="dialog"
        aria-modal="true"
        aria-labelledby="finding-hop-brief-title"
      >
        <header>
          <div className="finding-attack-story__kicker-row">
            <p className="finding-attack-story__tab">{badge.kicker}</p>
            <span className="finding-attack-story__stamp" data-stamp={badge.stamp}>
              {badge.stamp}
            </span>
          </div>
          <button
            type="button"
            className="finding-attack-story__x"
            onClick={onClose}
            aria-label="Close hop details"
          >
            <X size={16} />
          </button>
        </header>
        <h3 id="finding-hop-brief-title">{node.label}</h3>
        <p className="finding-attack-story__sub">
          <span>{node.role}</span>
          <span>{node.caption}</span>
        </p>
        <div className="finding-attack-story__pins">
          {fileLabel ? (
            fileHref ? (
              <a
                className="finding-attack-story__file"
                href={fileHref}
                target="_blank"
                rel="noreferrer"
                data-testid="finding-attack-file"
              >
                {fileLabel}
              </a>
            ) : (
              <span className="finding-attack-story__file">{fileLabel}</span>
            )
          ) : null}
          {node.ruleId ? (
            <a
              className="finding-attack-story__rule"
              href={ruleQueueHref(node.ruleId, scanId)}
              data-testid="finding-attack-rule"
            >
              {displayRuleId(node.ruleId)}
            </a>
          ) : null}
          {node.cwe ? (
            cweCatalogHref(node.cwe) ? (
              <a
                className="finding-attack-story__cwe"
                href={cweCatalogHref(node.cwe) as string}
                target="_blank"
                rel="noreferrer"
                data-testid="finding-attack-cwe"
              >
                {node.cwe}
              </a>
            ) : (
              <span className="finding-attack-story__cwe" data-testid="finding-attack-cwe">
                {node.cwe}
              </span>
            )
          ) : null}
        </div>
        <div className="finding-attack-story__brief-body">
          <p className="finding-attack-story__message">{node.detail}</p>
          <div className="finding-attack-story__observed">
            <span>Observed path</span>
            <p>
              Numbered chips are the sealed hops on this finding, left to right. Gold is this box.
              Same-scan discs on the map are not part of this strip.
            </p>
            <ol>
              {spine.map((step) => (
                <li key={step.id} className={step.here ? "is-here" : undefined}>
                  {step.hopNumber ? <small>{String(step.hopNumber).padStart(2, "0")}</small> : null}
                  {step.label}
                </li>
              ))}
            </ol>
          </div>
          <div className="finding-attack-story__metrics">
            <article className={`finding-attack-story__metric is-${proofTone}`}>
              <span>Proof</span>
              <strong>{badge.stamp}</strong>
              <small>{node.proof}</small>
            </article>
            <article className="finding-attack-story__metric">
              <span>Replay</span>
              <strong>{node.replay ?? "n/a"}</strong>
              <small>Identity of frozen bytes, not a live exploit</small>
            </article>
            <article className="finding-attack-story__metric">
              <span>Where</span>
              <strong>{fileLabel ?? "File not recorded for this hop"}</strong>
              <small>
                {node.ruleId ? (
                  <a href={ruleQueueHref(node.ruleId, scanId)}>{displayRuleId(node.ruleId)}</a>
                ) : (
                  "Recorded on this snapshot"
                )}
              </small>
            </article>
          </div>
          <CodeExcerpt lines={lines} empty={emptyExcerpt} />
          <div className="finding-attack-story__definition">
            <strong>What “security-sensitive operation” means</strong>
            <p>
              Security tools often call this a sink: a function where attacker-controlled input
              could trigger an unsafe effect such as command execution, a database query, file
              access, or a cryptographic decision. A sink alone is not proof that an attacker can
              reach it.
            </p>
          </div>
          <div className="finding-attack-story__callout is-takeaway">
            <span>Key takeaway</span>
            <p>
              {node.story}. {node.proof}
            </p>
          </div>
          <div className="finding-attack-story__callout is-implications">
            <span>Implications</span>
            <p>{node.brutal}</p>
          </div>
        </div>
        <footer>
          <button type="button" className="finding-attack-story__ghost" onClick={onClose}>
            Close
          </button>
          <button
            className="finding-attack-story__primary"
            type="button"
            data-testid="finding-attack-open-finding"
            onClick={openFinding}
          >
            Open this finding
          </button>
        </footer>
      </article>
    </div>
  );
}

export function shiftPoint(
  point: { x: number; y: number } | undefined,
  offset: { x: number; y: number } | undefined,
  bounds: { width: number; height: number },
): { x: number; y: number } | undefined {
  if (!point) return undefined;
  return {
    x: Math.min(bounds.width - 36, Math.max(36, point.x + (offset?.x ?? 0))),
    y: Math.min(bounds.height - 36, Math.max(36, point.y + (offset?.y ?? 0))),
  };
}

function curvePath(pts: { x: number; y: number }[]): string {
  if (pts.length < 2) return "";
  const first = pts[0];
  if (!first) return "";
  const parts = [`M ${first.x.toFixed(1)} ${first.y.toFixed(1)}`];
  for (let index = 1; index < pts.length; index += 1) {
    const from = pts[index - 1] as { x: number; y: number };
    const to = pts[index] as { x: number; y: number };
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    parts.push(
      `C ${(from.x + dx * 0.42).toFixed(1)} ${(from.y + dy * 0.08).toFixed(1)}, ${(from.x + dx * 0.58).toFixed(1)} ${(from.y + dy * 0.92).toFixed(1)}, ${to.x.toFixed(1)} ${to.y.toFixed(1)}`,
    );
  }
  return parts.join(" ");
}

const STORY_TRAVEL_SECONDS = 10;
const STORY_HOLD_SECONDS = 10;
const STORY_CYCLE_SECONDS = STORY_TRAVEL_SECONDS + STORY_HOLD_SECONDS;

function NarratedStory({ text }: { text: string }) {
  const words = text.split(/\s+/u).filter(Boolean);
  return (
    <>
      {words.map((word, index) => (
        <span className="finding-attack-story__word" key={words.slice(0, index + 1).join(" ")}>
          {word}
          {index < words.length - 1 ? " " : ""}
        </span>
      ))}
    </>
  );
}

function storyNarration(node: CompositionNode): string {
  return node.detail.replace(/(?:(?:[A-Za-z_$][\w$]*\.){2,})([A-Za-z_$][\w$]*)/gu, "$1");
}

export function FindingAttackStory({
  finding,
  related = [],
  onOpenFinding,
  inspectNodeId,
}: {
  finding: FindingView;
  related?: readonly FindingView[];
  onOpenFinding?: ((id: string) => void) | undefined;
  inspectNodeId?: string | undefined;
}) {
  const graph = useMemo(() => buildFindingCompositionGraph(finding, related), [finding, related]);
  const layout = useMemo(() => compositionLayout(graph), [graph]);
  const glowId = useId().replace(/:/g, "");
  const boardRef = useRef<HTMLDivElement>(null);
  const drag = useRef<{
    kind: "node" | "cloud";
    id: string;
    ox: number;
    oy: number;
    px: number;
    py: number;
    moved: boolean;
  } | null>(null);
  const skipClick = useRef(false);
  const [expanded, setExpanded] = useState(false);
  const [selected, setSelected] = useState<CompositionNode | null>(() =>
    inspectNodeId ? (graph.nodes.find((node) => node.id === inspectNodeId) ?? null) : null,
  );
  const [hover, setHover] = useState<{ node: CompositionNode; x: number; y: number } | null>(null);
  const [nodeOffsets, setNodeOffsets] = useState<Record<string, { x: number; y: number }>>({});
  const [cloudOffsets, setCloudOffsets] = useState<Record<string, { x: number; y: number }>>({});
  const [draggingId, setDraggingId] = useState<string | null>(null);

  // The graph position is intentionally reset when a different finding is displayed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: finding identity is the reset trigger.
  useEffect(() => {
    setNodeOffsets({});
    setCloudOffsets({});
  }, [finding.id]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setExpanded(false);
        setSelected(null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const placed = useMemo(() => {
    const next: Record<string, { x: number; y: number; node: CompositionNode }> = {};
    for (const [id, point] of Object.entries(layout.placed)) {
      const shifted = shiftPoint(point, nodeOffsets[id], layout);
      if (shifted) next[id] = { ...point, ...shifted };
    }
    return next;
  }, [layout, nodeOffsets]);

  const clouds = useMemo(() => {
    const next: Record<string, { x: number; y: number }> = {};
    for (const [id, point] of Object.entries(layout.clouds)) {
      const shifted = shiftPoint(
        point,
        {
          x: (nodeOffsets[id]?.x ?? 0) + (cloudOffsets[id]?.x ?? 0),
          y: (nodeOffsets[id]?.y ?? 0) + (cloudOffsets[id]?.y ?? 0),
        },
        layout,
      );
      if (shifted) next[id] = shifted;
    }
    return next;
  }, [layout, nodeOffsets, cloudOffsets]);

  const beginDrag = (event: PointerEvent<HTMLElement>, kind: "node" | "cloud", id: string) => {
    const current = kind === "node" ? nodeOffsets[id] : cloudOffsets[id];
    event.stopPropagation();
    drag.current = {
      kind,
      id,
      ox: current?.x ?? 0,
      oy: current?.y ?? 0,
      px: event.clientX,
      py: event.clientY,
      moved: false,
    };
    setDraggingId(kind === "cloud" ? `cloud:${id}` : id);
    setHover(null);
    try {
      event.currentTarget.setPointerCapture(event.pointerId);
    } catch {
      /* jsdom */
    }
  };

  const moveDrag = (event: PointerEvent<HTMLElement>) => {
    const start = drag.current;
    const board = boardRef.current;
    if (!start || !board) return;
    const box = board.getBoundingClientRect();
    const dx = box.width ? ((event.clientX - start.px) * layout.width) / box.width : 0;
    const dy = box.height ? ((event.clientY - start.py) * layout.height) / box.height : 0;
    if (Math.abs(dx) + Math.abs(dy) > 4) start.moved = true;
    const next = { x: start.ox + dx, y: start.oy + dy };
    if (start.kind === "node") {
      setNodeOffsets((current) => ({ ...current, [start.id]: next }));
    } else {
      setCloudOffsets((current) => ({ ...current, [start.id]: next }));
    }
  };

  const endDrag = () => {
    if (drag.current?.moved) skipClick.current = true;
    drag.current = null;
    setDraggingId(null);
  };

  const spineTrail = graph.spineIds
    .map((id) => graph.nodes.find((node) => node.id === id))
    .filter((node): node is CompositionNode => Boolean(node))
    .map((node) => ({
      id: node.id,
      label: node.label,
      hopNumber: node.hopNumber,
      here: selected?.id === node.id,
    }));
  const selectedExhibit = selected ? exhibitForNode(selected, finding, related) : null;
  const hoverExhibit = hover ? exhibitForNode(hover.node, finding, related) : null;

  const moveTip = (event: MouseEvent, node: CompositionNode) => {
    const pad = 16;
    const width = 320;
    const left = Math.min(event.clientX + 14, window.innerWidth - width - pad);
    const top = Math.min(event.clientY + 14, window.innerHeight - 260);
    setHover({ node, x: Math.max(pad, left), y: Math.max(pad, top) });
  };

  const spinePts = graph.spineIds
    .map((id) => placed[id])
    .filter((point): point is NonNullable<typeof point> => Boolean(point));
  const spineD = curvePath(spinePts);
  const layoutDirty = Object.keys(nodeOffsets).length + Object.keys(cloudOffsets).length > 0;

  useGSAP(
    () => {
      const board = boardRef.current;
      if (!board || !spineD) return;
      const path = board.querySelector<SVGPathElement>("[data-story-path]");
      const arrow = board.querySelector<SVGGElement>("[data-story-arrow]");
      const storyClouds = Array.from(board.querySelectorAll<HTMLElement>("[data-story-cloud]"));
      if (!path || !arrow || !storyClouds.length) return;

      const lengths = spinePts.slice(1).map((point, index) => {
        const previous = spinePts[index];
        return previous ? Math.hypot(point.x - previous.x, point.y - previous.y) : 0;
      });
      const totalLength = lengths.reduce((sum, length) => sum + length, 0);
      let traversed = 0;
      const stops = [
        0,
        ...lengths.map((length) => {
          traversed += length;
          return totalLength ? traversed / totalLength : 1;
        }),
      ];

      const resetStory = () => {
        for (const cloud of storyClouds) {
          delete cloud.dataset.storyState;
          delete cloud.dataset.storySpeaking;
          gsap.set(cloud.querySelectorAll(".finding-attack-story__word"), { autoAlpha: 0 });
        }
      };
      const showCompletedStory = () => {
        for (const cloud of storyClouds) {
          cloud.dataset.storyState = "revealed";
          delete cloud.dataset.storySpeaking;
          gsap.set(cloud.querySelectorAll(".finding-attack-story__word"), { autoAlpha: 1 });
        }
      };
      const placeArrow = (progress: number) => {
        gsap.set(arrow, {
          motionPath: {
            path,
            align: path,
            alignOrigin: [0.5, 0.5],
            autoRotate: true,
            start: progress,
            end: progress,
          },
          transformOrigin: "50% 50%",
        });
      };

      resetStory();
      placeArrow(0);
      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        showCompletedStory();
        placeArrow(1);
        return;
      }

      const secondsPerStep = STORY_TRAVEL_SECONDS / storyClouds.length;
      const arrowTravelSeconds = Math.min(0.72, secondsPerStep * 0.36);
      const timeline = gsap.timeline({ repeat: -1, repeatDelay: 0.3 });
      storyClouds.forEach((cloud, index) => {
        const stepAt = index * secondsPerStep;
        const words = Array.from(
          cloud.querySelectorAll<HTMLElement>(".finding-attack-story__word"),
        );
        timeline.call(
          () => {
            const previous = storyClouds[index - 1];
            if (previous) delete previous.dataset.storySpeaking;
            cloud.dataset.storyState = "revealed";
            cloud.dataset.storySpeaking = "true";
          },
          [],
          stepAt,
        );
        if (words.length) {
          timeline.set(
            words,
            {
              autoAlpha: 1,
              stagger: Math.max(0.06, (secondsPerStep - 0.68) / words.length),
            },
            stepAt + 0.52,
          );
        }
        const nextStop = stops[index + 1];
        if (nextStop !== undefined) {
          timeline.to(
            arrow,
            {
              duration: arrowTravelSeconds,
              ease: "power2.inOut",
              motionPath: {
                path,
                align: path,
                alignOrigin: [0.5, 0.5],
                autoRotate: true,
                start: stops[index] ?? 0,
                end: nextStop,
              },
            },
            stepAt + secondsPerStep - arrowTravelSeconds,
          );
        }
      });
      timeline.call(
        () => {
          const finalCloud = storyClouds.at(-1);
          if (finalCloud) delete finalCloud.dataset.storySpeaking;
        },
        [],
        STORY_TRAVEL_SECONDS,
      );
      timeline.call(resetStory, [], STORY_CYCLE_SECONDS);

      const observer = new IntersectionObserver(([entry]) => {
        if (entry?.isIntersecting) timeline.resume();
        else timeline.pause();
      });
      observer.observe(board);
      return () => observer.disconnect();
    },
    {
      scope: boardRef,
      dependencies: [finding.id, spineD],
      revertOnUpdate: true,
    },
  );

  return (
    <section
      className={expanded ? "finding-attack-story is-expanded" : "finding-attack-story"}
      data-testid="finding-attack-story"
      aria-label="Scan composition map"
    >
      <div className="finding-attack-story__header">
        <div>
          <span className="eyebrow">Attacker route on this snapshot</span>
          <h3>How input can reach the security-sensitive operation</h3>
        </div>
        <div className="finding-attack-story__tools">
          <span>
            {graph.relatedCount} same-scan finding{graph.relatedCount === 1 ? "" : "s"} ·{" "}
            {graph.recordedHops} recorded hop{graph.recordedHops === 1 ? "" : "s"}
          </span>
          <button
            className="finding-attack-story__expand"
            type="button"
            disabled={!layoutDirty}
            onClick={() => {
              setNodeOffsets({});
              setCloudOffsets({});
            }}
          >
            <RotateCcw size={16} />
            Reset layout
          </button>
          <button
            className="finding-attack-story__expand"
            type="button"
            onClick={() => setExpanded((current) => !current)}
          >
            {expanded ? <X size={16} /> : <Maximize2 size={16} />}
            {expanded ? "Exit full screen" : "Full screen"}
          </button>
        </div>
      </div>
      <p className="finding-attack-story__honesty">{graph.honesty}</p>
      <div
        ref={boardRef}
        className="finding-attack-story__board"
        data-testid="finding-attack-board"
        style={{ aspectRatio: `${layout.width} / ${layout.height}` }}
      >
        <svg viewBox={`0 0 ${layout.width} ${layout.height}`} aria-hidden="true">
          <defs>
            <filter id={`fas-glow-${glowId}`} x="-40%" y="-40%" width="180%" height="180%">
              <feGaussianBlur stdDeviation="2.8" result="bloom" />
              <feMerge>
                <feMergeNode in="bloom" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>
          {layout.zones.map((zone) => (
            <g key={zone.id}>
              <rect
                className="finding-attack-story__zone"
                x={zone.x}
                y={zone.y}
                width={zone.width}
                height={zone.height}
                rx="12"
              />
              <text className="finding-attack-story__zone-label" x={zone.x + 16} y={zone.y + 22}>
                {zone.label}
              </text>
            </g>
          ))}
          {graph.edges
            .filter((edge) => edge.flow !== "spine")
            .map((edge) => {
              const from = placed[edge.from];
              const to = placed[edge.to];
              if (!from || !to) return null;
              const midX = (from.x + to.x) / 2;
              const d = `M ${from.x} ${from.y} C ${midX} ${from.y}, ${midX} ${to.y}, ${to.x} ${to.y}`;
              return (
                <path
                  key={edge.id}
                  className={`finding-attack-story__edge finding-attack-story__edge--${edge.flow}`}
                  d={d}
                />
              );
            })}
          {spineD ? (
            <g className="finding-attack-story__glow">
              <path className="finding-attack-story__halo" d={spineD} />
              <path className="finding-attack-story__core" d={spineD} data-story-path />
              <path className="finding-attack-story__spine-flow" d={spineD} pathLength={100} />
              <g
                className="finding-attack-story__arrow"
                data-story-arrow
                filter={`url(#fas-glow-${glowId})`}
              >
                <polygon points="-18,-9 20,0 -18,9 -10,0" />
              </g>
            </g>
          ) : null}
          {graph.spineIds.map((id) => {
            const cloud = clouds[id];
            const node = placed[id];
            if (!cloud || !node) return null;
            return (
              <path
                key={`lead-${id}`}
                className="finding-attack-story__leader"
                d={`M ${cloud.x} ${cloud.y + 26} L ${node.x} ${node.y - 22}`}
              />
            );
          })}
        </svg>
        {graph.spineIds.map((id) => {
          const cloud = clouds[id];
          const node = graph.nodes.find((item) => item.id === id);
          if (!cloud || !node) return null;
          return (
            <button
              key={`cloud-${id}`}
              type="button"
              className={
                draggingId === `cloud:${id}`
                  ? "finding-attack-story__cloud is-dragging"
                  : "finding-attack-story__cloud"
              }
              data-story-cloud
              data-testid="finding-attack-cloud"
              data-tone={node.tone}
              aria-label={`Move story for ${node.label}`}
              style={{
                left: `${(cloud.x / layout.width) * 100}%`,
                top: `${(cloud.y / layout.height) * 100}%`,
              }}
              onPointerDown={(event) => beginDrag(event, "cloud", id)}
              onPointerMove={moveDrag}
              onPointerUp={endDrag}
              onPointerCancel={endDrag}
              onClick={() => {
                if (skipClick.current) {
                  skipClick.current = false;
                  return;
                }
                setHover(null);
                setSelected(node);
              }}
            >
              <span className="finding-attack-story__cloud-copy">
                <NarratedStory text={storyNarration(node)} />
              </span>
            </button>
          );
        })}
        {graph.nodes.map((node) => {
          const point = placed[node.id];
          if (!point) return null;
          const hop = node.hopNumber ? String(node.hopNumber).padStart(2, "0") : "";
          return (
            <button
              key={node.id}
              type="button"
              className={[
                "finding-attack-story__node",
                node.focus ? "is-focus" : "",
                selected?.id === node.id ? "is-open" : "",
                draggingId === node.id ? "is-dragging" : "",
              ]
                .filter(Boolean)
                .join(" ")}
              data-tone={node.tone}
              style={{
                left: `${(point.x / layout.width) * 100}%`,
                top: `${(point.y / layout.height) * 100}%`,
              }}
              onPointerDown={(event) => beginDrag(event, "node", node.id)}
              onPointerMove={moveDrag}
              onPointerUp={endDrag}
              onPointerCancel={endDrag}
              onMouseEnter={(event) => {
                if (!drag.current) moveTip(event, node);
              }}
              onMouseMove={(event) => {
                if (!drag.current) moveTip(event, node);
              }}
              onMouseLeave={() => setHover(null)}
              onClick={() => {
                if (skipClick.current) {
                  skipClick.current = false;
                  return;
                }
                setHover(null);
                setSelected(node);
              }}
            >
              <span className="finding-attack-story__disc">{hop}</span>
              <span className="finding-attack-story__title">{node.label}</span>
            </button>
          );
        })}
      </div>
      {hover ? (
        <aside
          className="finding-attack-story__tip"
          data-testid="finding-attack-hover-tip"
          style={{ left: hover.x, top: hover.y }}
        >
          <span>{hover.node.role}</span>
          <p className="finding-attack-story__tip-title">{hover.node.label}</p>
          <p>{hover.node.story}</p>
          <dl className="finding-attack-story__tip-table">
            <div>
              <dt>Hop file</dt>
              <dd>{pathEvidence(hover.node, finding).hopFile}</dd>
            </div>
            <div>
              <dt>Cited finding</dt>
              <dd>{pathEvidence(hover.node, finding).citedFinding}</dd>
            </div>
            <div>
              <dt>Evidence</dt>
              <dd>{pathEvidence(hover.node, finding).status}</dd>
            </div>
          </dl>
          {hoverExhibit ? (
            <CodeExcerpt lines={hoverExhibit.lines} empty={excerptEmpty(hover.node, finding)} />
          ) : null}
          <p className="finding-attack-story__tip-hint">
            Drag to move · click for the hop briefing
          </p>
        </aside>
      ) : null}
      {selected && selectedExhibit ? (
        <NodeDossier
          node={selected}
          spine={spineTrail}
          fileHref={fileHrefForNode(selected, finding, related)}
          lines={selectedExhibit.lines}
          emptyExcerpt={excerptEmpty(selected, finding)}
          scanId={finding.scanId}
          onClose={() => setSelected(null)}
          onOpenFinding={onOpenFinding}
        />
      ) : null}
      <div className="finding-attack-story__break">
        <span>Break the path here</span>
        <p>{graph.breakSentence}</p>
      </div>
      <ul className="finding-attack-story__legend">
        <li data-tone="entry">Numbered discs: sealed hops on this finding</li>
        <li data-tone="sink">
          Gold glow: recorded route to the security-sensitive operation (“sink”)
        </li>
        <li data-tone="sibling">Lapis dash: same-scan finding, not a proven hop</li>
        <li data-flow="out">Sienna dash: outflow correlation</li>
        <li>Drag a hop or story cloud to make space. Reset layout restores the default.</li>
      </ul>
    </section>
  );
}
